/**
 * Permission Protected Route
 * 
 * Route Guard ที่ตรวจสอบทั้ง authentication, permissions และ feature flags
 * ถ้าไม่มีสิทธิ์เข้าถึง → redirect ไปหน้า dashboard หรือ 403
 */

import { Navigate, useLocation } from 'react-router-dom'
import { usePermission } from '@/hooks/use-permission'
import { ProtectedRoute } from './protected-route'
import { isRouteEnabled } from '@/config/features.config'

interface PermissionProtectedRouteProps {
  children: React.ReactNode
  requiredPath: string // Path ที่ต้องการตรวจสอบสิทธิ์ (เช่น '/members', '/settings')
  fallbackPath?: string // Path ที่จะ redirect ถ้าไม่มีสิทธิ์ (default: '/dashboard')
}

/**
 * Permission Protected Route Component
 * 
 * ตรวจสอบทั้ง authentication, permissions และ feature flags
 * - ถ้า feature ถูกปิด (dev feature ใน production) → redirect ไป fallbackPath
 * - ถ้ายังไม่ login → redirect ไป /login
 * - ถ้า login แล้วแต่ไม่มีสิทธิ์ → redirect ไป fallbackPath (default: /dashboard)
 */
export function PermissionProtectedRoute({
  children,
  requiredPath,
  fallbackPath = '/dashboard',
}: PermissionProtectedRouteProps) {
  const location = useLocation()
  const { canAccessMenu } = usePermission()

  // ตรวจสอบ feature flag ก่อน (dev features จะถูกปิดใน production)
  if (!isRouteEnabled(requiredPath)) {
    return (
      <Navigate
        to={fallbackPath}
        replace
        state={{ from: location, reason: 'feature_disabled' }}
      />
    )
  }

  return (
    <ProtectedRoute>
      {canAccessMenu(requiredPath) ? (
        children
      ) : (
        <Navigate
          to={fallbackPath}
          replace
          state={{ from: location, reason: 'no_permission' }}
        />
      )}
    </ProtectedRoute>
  )
}
